import { type ReactElement, type ReactNode, useEffect, useMemo, useState } from 'react';
import { breadcrumbFor, filterNavByGrants, navBadges, navLeaves, textMatches, type NavCountBadge, type NavTreeNode } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { Kbd, PopoverAnchor } from '@vello/web-theme';
import { CommandPalette, Popover, type PaletteSection } from '@vello/web-theme';
import { UiIcon, type NavIconName } from '@vello/web-theme';
import { Breadcrumb, ConsoleShell, SidebarNav, initialsOf, useAccountLanguage, type BreadcrumbItem, type SidebarNavItem } from '@vello/web-theme';
import { LANGS } from '../i18n/messages';
import { OperatorAlertsBadge, OperatorAlertsList, SetupAlertRow, useOperatorAlerts } from './operator-alerts';
import { useSetupPending } from './setup-readiness-signal';
import { useOpsCounts } from './ops-counts';
import { usePhoneText } from './phone-text';
import { AdminScopeSwitcher } from './scope-menu';
import { AuthShowcase } from '@vello/web-theme';
import loginScene from '../assets/login-scene.webp';

/**
 * The web-shell standard (Claude Design "Admin Web - Passageiros Interativo", shell 1a — the navigation standard
 * for the Admin Web and every later Vello web system): a navy sidebar with the icon-led, up-to-three-level tree
 * (white active pill, count badges, operator card) that collapses to a 68px icon rail (persisted per browser); a
 * 56px top bar with the breadcrumb, the ⌘K global search, the scope selector and notifications; full-width white
 * content with a fixed 32px gutter. Reproduced from the design, not reinterpreted; surfaces the design did not
 * draw (registries, integrations, audit, the D318 consoles) are completed as leaves of the same tree.
 */
export interface NavNode extends NavTreeNode {
  readonly icon: NavIconName;
  readonly children?: readonly NavNode[];
  /** A count badge — only ever from a real server count (D358: the tree used to carry invented numbers). */
  readonly badge?: { readonly count: number; readonly tone: 'danger' | 'warning' };
  /** A top-level alert dot on the collapsed rail — only for a real alert, never decorative. */
  readonly dot?: boolean;
}

export const NAV_TREE: readonly NavNode[] = [
  { id: 'overview', labelId: 'nav.overview', icon: 'dashboard', route: 'dashboard' },
  // D411 (Admin #61): every queue where a change waits for a second person, one line each.
  { id: 'four_eyes_inbox', labelId: 'nav.fourEyesInbox', icon: 'userCheck', route: 'four_eyes_inbox' },
  {
    id: 'operations',
    labelId: 'nav.operations',
    icon: 'activity',
    // D358: no invented counts. The fabricated rail dot and the "2" / "12" badges were removed; a badge returns only
    // when it reads a real scoped count from the server (D560: `GET /v1/ops/counts`, mapped in `NAV_COUNT_SOURCES`).
    children: [
      { id: 'live_rides', labelId: 'nav.liveRides', icon: 'map', route: 'live_map' },
      // D472 (A-11): the ride grid with the audited batch cancel; it is also the support lookup (an id, an account or a phone) — "Suporte a corridas" was merged into it.
      { id: 'rides_grid', labelId: 'nav.ridesGrid', icon: 'carFront', route: 'rides_grid' },
      { id: 'dispatch', labelId: 'nav.dispatch', icon: 'navigation', route: 'dispatch' },
      { id: 'courier_jobs', labelId: 'nav.courierJobs', icon: 'mapPin', route: 'courier_jobs' },
      { id: 'incidents', labelId: 'nav.incidents', icon: 'shield', route: 'cases' },
      { id: 'disputes', labelId: 'nav.disputes', icon: 'scale', route: 'disputes' },
      { id: 'safety', labelId: 'nav.safety', icon: 'alertTriangle', route: 'safety' },
      // D420: the drivers' SOS and trip reports (read only; the owner decision on D416).
      { id: 'ops_incidents', labelId: 'nav.opsIncidents', icon: 'bell', route: 'ops_incidents' },
      // D471 (A-29): the route-anomaly alerts the worker raises.
      { id: 'route_anomalies', labelId: 'nav.routeAnomalies', icon: 'navigation', route: 'route_anomalies' },
      // D474: the risk areas drawn on the map, and the platform's alert center.
      { id: 'risk_areas', labelId: 'nav.riskAreas', icon: 'shield', route: 'risk_areas' },
      { id: 'alert_center', labelId: 'nav.alertCenter', icon: 'bell', route: 'alert_center' },
      // D488: the incentive zones the market signals are counted in.
      { id: 'incentive_zones', labelId: 'nav.incentiveZones', icon: 'mapPin', route: 'incentive_zones' },
      // D616: the area the market declares it serves, drawn on the map and published under four eyes.
      { id: 'service_areas', labelId: 'nav.serviceAreas', icon: 'globe', route: 'service_areas' },
      // D674: the shared base of addresses and named places, beside the service area and the markets it is filed under.
      { id: 'places', labelId: 'nav.places', icon: 'mapPin', route: 'places' },
      // D561: the market's registered devices - the operations-room TV and the lobby kiosk - paired with a single-use code.
      { id: 'devices', labelId: 'nav.devices', icon: 'plug', route: 'devices' },
      // D647: the operations-room TVs - one link per screen, with the views it shows and the scope it covers.
      { id: 'tv_panels', labelId: 'nav.tvPanels', icon: 'activity', route: 'tv_panels' },
      // D473: the central speaks in a ride's chat.
      { id: 'ride_chats', labelId: 'nav.rideChats', icon: 'users', route: 'ride_chats' },
      // D632: the Central's teams per country (function, hours in each market's own zone, members).
      { id: 'central_teams', labelId: 'nav.centralTeams', icon: 'users', route: 'central_teams' },
      // D636: the WhatsApp numbers the Central talks to customers through, each with the country it serves.
      { id: 'central_whatsapp', labelId: 'nav.centralWhatsapp', icon: 'plug', route: 'central_whatsapp' },
      // D478: suspensions with a start and an end, and the relationships console (occurrences, ratings, blocks).
      { id: 'suspensions', labelId: 'nav.suspensions', icon: 'alertTriangle', route: 'reputation_suspensions' },
      { id: 'relationships', labelId: 'nav.relationships', icon: 'users', route: 'reputation_relationships' },
    ],
  },
  {
    id: 'send',
    labelId: 'nav.send',
    icon: 'transfer',
    children: [
      { id: 'send_deliveries', labelId: 'nav.sendDeliveries', icon: 'mapPin', route: 'send_deliveries' },
      { id: 'send_claims', labelId: 'nav.sendClaims', icon: 'scale', route: 'send_claims' },
    ],
  },
  {
    id: 'providers',
    labelId: 'nav.providers',
    icon: 'briefcase',
    children: [
      // D513 (C-031): the providers' dashboard (counts by status, the review queue, the supply now).
      { id: 'providers_overview', labelId: 'nav.providersOverview', icon: 'barChart', route: 'providers_overview' },
      // D619 (owner 2026-10-18): one provider account drives a car, a moto, delivers food or parcels on different days — the
      // area is the providers', not a "Motoristas" sub-group (that name read as car drivers only).
      { id: 'driver_directory', labelId: 'nav.driverDirectory', icon: 'users', route: 'driver_directory' },
      { id: 'driver_approvals', labelId: 'nav.driverApprovals', icon: 'userCheck', route: 'driver_approvals' },
      { id: 'driver_documents', labelId: 'nav.driverDocuments', icon: 'fileCheck', route: 'driver_documents' },
      // D481: the market's driver registration grid and the central document query / approval queue.
      { id: 'driver_registry', labelId: 'nav.driverRegistry', icon: 'userCheck', route: 'driver_registry' },
      { id: 'driver_documents_all', labelId: 'nav.driverDocumentsAll', icon: 'fileCheck', route: 'driver_documents_all' },
      // D883 (program D871 phase 5): each provider's request for a service line, waiting for the team.
      { id: 'provider_services', labelId: 'nav.providerServices', icon: 'userCheck', route: 'provider_services' },
      // D437: the vehicles the market's drivers registered, and their operator inspection.
      { id: 'driver_vehicles', labelId: 'nav.driverVehicles', icon: 'carFront', route: 'driver_vehicles' },
      { id: 'driver_groups', labelId: 'nav.driverGroups', icon: 'users', route: 'driver_groups' },
      // D477 (F-051, I-053): the goal campaigns that credit the driver's wallet.
      { id: 'driver_awards', labelId: 'nav.awards', icon: 'banknote', route: 'awards' },
    ],
  },
  {
    id: 'customers',
    labelId: 'nav.customers',
    icon: 'users',
    children: [
      // D513 (C-031): the customers' dashboard (passengers and companies by status).
      { id: 'customers_overview', labelId: 'nav.customersOverview', icon: 'barChart', route: 'customers_overview' },
      { id: 'passengers', labelId: 'nav.passengers', icon: 'user', route: 'passengers' },
      // D475 (E-001/E-005, X-09): register a rider; the phone change under four eyes.
      { id: 'passenger_registry', labelId: 'nav.passengerRegistry', icon: 'users', route: 'passenger_registry' },
      { id: 'companies', labelId: 'nav.companies', icon: 'building', route: 'companies' },
    ],
  },
  // D522 (AN-BI-UI, doc 20 §4.3): Análises — Vello's own BI (standard panels, explorer, saved reports); shown only with `analytics:view`.
  {
    id: 'analyses',
    labelId: 'nav.analyses',
    icon: 'barChart',
    children: [
      { id: 'bi_panels', labelId: 'nav.biPanels', icon: 'dashboard', route: 'bi_panels' },
      { id: 'bi_explorer', labelId: 'nav.biExplorer', icon: 'trendingUp', route: 'bi_explorer' },
      { id: 'bi_reports', labelId: 'nav.biReports', icon: 'receipt', route: 'bi_reports' },
      { id: 'bi_exports', labelId: 'nav.biExports', icon: 'fileCheck', route: 'bi_exports' },
      // D527: the pending reports (driver km, customers, companies, requests, quotes by provider, ads) and the access report.
      { id: 'bi_lists', labelId: 'nav.biLists', icon: 'fileCheck', route: 'bi_lists' },
      { id: 'access_report', labelId: 'nav.access', icon: 'shield', route: 'access_report' },
    ],
  },
  {
    id: 'finance',
    labelId: 'nav.finance',
    icon: 'banknote',
    children: [
      { id: 'ledger', labelId: 'nav.ledger', icon: 'receipt', route: 'finance' },
      { id: 'payouts', labelId: 'nav.payouts', icon: 'transfer', route: 'finance_payouts' },
      { id: 'partner_billing', labelId: 'nav.partnerBilling', icon: 'building', route: 'finance_partner_billing' },
      { id: 'merchant_statements', labelId: 'nav.merchantStatements', icon: 'receipt', route: 'finance_merchant_statements' },
      { id: 'refund_approvals', labelId: 'nav.refundApprovals', icon: 'transfer', route: 'finance_refund_approvals' },
      { id: 'cash_handovers', labelId: 'nav.cashHandovers', icon: 'banknote', route: 'finance_cash_handovers' },
      { id: 'driver_debts', labelId: 'nav.driverDebts', icon: 'receipt', route: 'finance_driver_debts' },
      { id: 'rider_debts', labelId: 'nav.riderDebts', icon: 'receipt', route: 'finance_rider_debts' }, // D536
      { id: 'rider_cash_blocks', labelId: 'nav.riderCashBlocks', icon: 'shield', route: 'finance_rider_cash_blocks' }, // D536
      { id: 'wallets', labelId: 'nav.wallets', icon: 'banknote', route: 'finance_wallets' },
      { id: 'rider_wallet', labelId: 'nav.riderWallet', icon: 'banknote', route: 'finance_rider_wallet' }, // D737b
      { id: 'incentive_fund', labelId: 'nav.incentiveFund', icon: 'banknote', route: 'finance_incentive_fund' }, // D489b
      { id: 'incentive_offers', labelId: 'nav.incentiveOffers', icon: 'banknote', route: 'finance_incentive_offers' }, // D490
      { id: 'incentive_controller', labelId: 'nav.incentiveController', icon: 'banknote', route: 'finance_incentive_controller' }, // D495
      { id: 'coupon_campaigns', labelId: 'nav.couponCampaigns', icon: 'banknote', route: 'finance_coupon_campaigns' }, // D493
      { id: 'settlements', labelId: 'nav.settlement', icon: 'receipt', route: 'finance_settlement' },
      {
        id: 'tariffs',
        labelId: 'nav.tariffs',
        icon: 'sliders',
        children: [
          { id: 'tariff_categories', labelId: 'nav.tariffCategories', icon: 'carFront', route: 'tariff_categories' },
          { id: 'tariff_dynamic', labelId: 'nav.tariffDynamic', icon: 'trendingUp', route: 'tariff_dynamic' },
          { id: 'service_categories', labelId: 'nav.serviceCategories', icon: 'carFront', route: 'service_categories' }, // D462
        ],
      },
    ],
  },
  { id: 'campaigns', labelId: 'nav.campaigns', icon: 'ticket', route: 'campaigns' },
  // D723 (doc 29 G12): Indicações - the rider and driver referral campaigns.
  { id: 'referrals', labelId: 'nav.referrals', icon: 'users', route: 'referrals' },
  // D477 (H-014, H-018): the mass broadcast and the notification banners.
  {
    id: 'communication',
    labelId: 'nav.communication',
    icon: 'bell',
    children: [
      { id: 'broadcasts', labelId: 'nav.broadcasts', icon: 'bell', route: 'broadcasts' },
      { id: 'banners', labelId: 'nav.banners', icon: 'ticket', route: 'banners' },
    ],
  },
  {
    id: 'food',
    labelId: 'nav.food',
    icon: 'utensils',
    children: [
      { id: 'food_orders', labelId: 'nav.foodOrders', icon: 'receipt', route: 'food_orders' },
      { id: 'food_ops', labelId: 'nav.foodOps', icon: 'activity', route: 'food_ops' },
      { id: 'food_stores', labelId: 'nav.foodStores', icon: 'building', route: 'food_stores' },
      { id: 'food_postsale', labelId: 'nav.foodPostsale', icon: 'scale', route: 'food_postsale' },
      { id: 'food_review', labelId: 'nav.foodReview', icon: 'fileCheck', route: 'food_review' },
      { id: 'food_ads', labelId: 'nav.foodAds', icon: 'ticket', route: 'food_ads' },
      { id: 'food_fulfillment', labelId: 'nav.foodFulfillment', icon: 'sliders', route: 'food_fulfillment' },
    ],
  },
  {
    id: 'settings',
    labelId: 'nav.settings',
    icon: 'settings',
    children: [
      // D615 (SETUP-READINESS): what is still missing for the platform to work; its badge is the pending count.
      { id: 'platform_setup', labelId: 'nav.platformSetup', icon: 'fileCheck', route: 'platform_setup' },
      { id: 'registries', labelId: 'nav.registries', icon: 'globe', route: 'registries' },
      { id: 'markets', labelId: 'nav.markets', icon: 'mapPin', route: 'markets' },
      { id: 'integrations', labelId: 'nav.integrations', icon: 'plug', route: 'integrations' },
      // D429: the Central widget's embeds, beside the provider integrations they belong with.
      { id: 'integrations_widgets', labelId: 'nav.widgets', icon: 'globe', route: 'integrations_widgets' },
      { id: 'security', labelId: 'nav.security', icon: 'shield', route: 'security' },
      // D449 (A-05/A-06): the staff directory, and every operator's own second factor.
      { id: 'operators', labelId: 'nav.operators', icon: 'users', route: 'operators' },
      // D475 (A-018/A-019): a subset of a role's permissions, with a home screen.
      { id: 'access_profiles', labelId: 'nav.accessProfiles', icon: 'shield', route: 'access_profiles' },
      // D597 (MAN-ACCESS): who reads the closed sections of the platform manual.
      { id: 'manual_access', labelId: 'nav.manualAccess', icon: 'fileCheck', route: 'manual_access' },
      { id: 'my_access', labelId: 'nav.myAccess', icon: 'shield', route: 'my_access' },
      { id: 'governed_settings', labelId: 'nav.governedSettings', icon: 'sliders', route: 'governed_settings' },
      // D631: the Central's assistant, its money and payment options, and the AI it runs on - by country.
      { id: 'central_ai_settings', labelId: 'nav.centralAiSettings', icon: 'sliders', route: 'central_ai_settings' },
      // D639: what the AI cost this month, the forecast and the budget.
      { id: 'ai_spend', labelId: 'nav.aiSpend', icon: 'barChart', route: 'ai_spend' },
      // D874: what Google and HERE bill this month, beside the AI spend.
      { id: 'maps_spend', labelId: 'nav.mapsSpend', icon: 'barChart', route: 'maps_spend' },
      // D437: the documents a country requires of its drivers and couriers (the KYC catalog, four eyes).
      { id: 'kyc_catalog', labelId: 'nav.kycCatalog', icon: 'fileCheck', route: 'kyc_catalog' },
      // D440: the versioned terms / privacy policy (four-eyes publish).
      { id: 'legal_documents', labelId: 'nav.legalDocuments', icon: 'fileCheck', route: 'legal_documents' },
      // The institutional site's job openings.
      { id: 'career_openings', labelId: 'nav.careerOpenings', icon: 'briefcase', route: 'career_openings' },
      { id: 'notifications', labelId: 'nav.notifications', icon: 'bell', route: 'notifications' },
      { id: 'analytics', labelId: 'nav.reports', icon: 'barChart', route: 'analytics' },
      // D472 (P-07): the exportable report catalogue.
      { id: 'report_catalog', labelId: 'nav.reportCatalog', icon: 'receipt', route: 'report_catalog' },
      { id: 'audit', labelId: 'nav.audit', icon: 'history', route: 'audit' },
      // D472 (A-14): the global audit trail.
      { id: 'audit_trail', labelId: 'nav.auditTrail', icon: 'history', route: 'audit_trail' },
      { id: 'about', labelId: 'nav.about', icon: 'info', route: 'about' },
    ],
  },
];

/** Record routes fold into their list route for navigation state (the sidebar highlights the list). */
const LIST_ROUTE_OF: Readonly<Record<string, string>> = { passenger_detail: 'passengers', driver_detail: 'driver_directory', integrations_otp_setup: 'integrations', ride_detail: 'rides_grid', rides: 'rides_grid', case_detail: 'cases' };

/**
 * Frame for the unauthenticated routes (login, recovery, session expired): the shared showcase sign-in (Claude Design
 * "16 Login - Padrões" 1B) — the navy panel with the form beside the Admin scene. There is no language control: the
 * console language is resolved automatically from the browser/OS (see `i18n/provider`).
 */
export function AuthLayout({ children }: { children: ReactNode }): ReactElement {
  return <AuthShowcase scene={loginScene}>{children}</AuthShowcase>;
}


/** Sidebar model: the nav tree as the library's `SidebarNavItem`s (ids are the tree's, routes stay in `NavNode`). */
function toNavItems(nodes: readonly NavNode[], t: (id: string) => string, badges: ReadonlyMap<string, NavCountBadge>, badgeLabel: string): SidebarNavItem[] {
  return nodes.map((node) => {
    // D560: the badge is the server's real count for the queue this leaf lists (a group: the sum of its leaves) — the static
    // `badge` of a node (none today) is only a fallback; a queue not counted for this operator gets no badge.
    const counted = badges.get(node.id);
    const badge = counted !== undefined ? { ...counted, label: badgeLabel } : node.badge;
    return {
      id: node.id,
      label: t(node.labelId),
      icon: node.icon,
      ...(node.children !== undefined ? { children: toNavItems(node.children, t, badges, badgeLabel) } : {}),
      ...(badge !== undefined ? { badge } : {}),
      ...(node.dot === true ? { dot: true } : {}),
    };
  });
}

/** id → route of every leaf, and the first leaf id of each route (the sidebar's `activeId` is a leaf id). */
function indexLeaves(nodes: readonly NavNode[], routeById: Map<string, string>, idByRoute: Map<string, string>): void {
  for (const node of nodes) {
    if (node.children !== undefined) indexLeaves(node.children, routeById, idByRoute);
    else if (node.route !== undefined) {
      routeById.set(node.id, node.route);
      if (!idByRoute.has(node.route)) idByRoute.set(node.route, node.id);
    }
  }
}

/** Authenticated console chrome: the library `ConsoleShell` + `SidebarNav` (shell 1a), the breadcrumb, ⌘K, scope and bell. */
export function AppShell({ children }: { children: ReactNode }): ReactElement {
  const { me, grants, logout } = useAdmin();
  const { route, navigate } = useRouter();
  const { sidebarCollapsed, toggleSidebar, paletteOpen, openPalette, closePalette, setDirectoryQuery, recordTitle } = useConsole();
  const { t, lang, setLang } = useT();
  // D806 (doc 35 §4): after sign-in the language saved on the account wins over the browser's.
  useAccountLanguage(me?.locale, LANGS, lang, setLang);
  const phoneOf = usePhoneText();
  const navRoute = LIST_ROUTE_OF[route] ?? route;
  // D364: the menu lists only what this operator's grants open. Before this, every persona was offered all 27
  // screens and most of them answered 403. The server still authorizes every call; this only stops the console
  // offering a screen it knows is refused.
  const navTree = useMemo(() => filterNavByGrants(NAV_TREE, grants), [grants]);
  // D560: one poll of `GET /v1/ops/counts` feeds the menu badges and the bell.
  const opsCounts = useOpsCounts();
  // D615 (SETUP-READINESS): the platform setup's pending items badge their own leaf (and the group above); the bell adds them too.
  const setup = useSetupPending();
  const setupPending = setup.pending !== undefined && setup.pending > 0 ? setup.pending : 0;
  const badges = useMemo(
    () => navBadges(navTree, opsCounts.status === 'ready' ? opsCounts.values : undefined, setupPending > 0 ? { platform_setup: { count: setupPending, atLeast: false } } : undefined),
    [navTree, opsCounts, setupPending],
  );
  const navItems = useMemo(() => toNavItems(navTree, t, badges, t('nav.badge.pending')), [navTree, t, badges]);
  const { routeById, idByRoute } = useMemo(() => {
    const routeById = new Map<string, string>();
    const idByRoute = new Map<string, string>();
    indexLeaves(navTree, routeById, idByRoute);
    return { routeById, idByRoute };
  }, [navTree]);

  const [bellOpen, setBellOpen] = useState(false);
  // D401: the bell's own reads (the six operator queues). It lives in the chrome, so every screen keeps it live.
  const alerts = useOperatorAlerts(opsCounts);
  const [paletteQuery, setPaletteQuery] = useState('');

  useEffect(() => {
    if (!paletteOpen) setPaletteQuery('');
  }, [paletteOpen]);

  const operatorName = [me?.firstName, me?.lastName].filter((p) => p !== undefined && p !== '').join(' ') || me?.email || me?.id || t('header.operator');
  const operatorMail = me?.email ?? (me?.phoneE164 !== undefined ? phoneOf(me.phoneE164) : '');
  const initials = initialsOf(operatorName, t('header.operator').slice(0, 1).toUpperCase());
  // D854 (L7a-015): the breadcrumb names the page from the WHOLE tree - a screen the grants hide from the menu (the access-denied
  // page of a deep link) still says which screen it is, instead of "Início > Início".
  const crumb = breadcrumbFor(NAV_TREE, navRoute);
  const unknownCrumb = crumb.labelId === 'nav.home';
  const isRecord = LIST_ROUTE_OF[route] !== undefined;

  // Palette: screens (from the nav tree) + actions + a hand-off into the directories for a people search.
  const q = paletteQuery.trim();
  const leaves = navLeaves(navTree);
  const pages = (q === '' ? leaves.slice(0, 6) : leaves.filter((l) => textMatches(t(l.labelId), q) || textMatches(t(l.parentLabelId), q))).slice(0, 6);
  const sections: PaletteSection[] = [
    ...(q !== ''
      ? [
          {
            id: 'people',
            title: t('palette.people'),
            items: [
              { id: 'people-passengers', label: t('palette.searchIn', { q, where: t('nav.passengers') }), icon: 'userSearch' as const, onSelect: () => { setDirectoryQuery(q); closePalette(); navigate('passengers'); } },
              { id: 'people-drivers', label: t('palette.searchIn', { q, where: t('nav.providers') }), icon: 'userSearch' as const, onSelect: () => { setDirectoryQuery(q); closePalette(); navigate('driver_directory'); } },
            ],
          },
        ]
      : []),
    { id: 'pages', title: t('palette.pages'), items: pages.map((l) => ({ id: `page-${l.route}`, label: t(l.labelId), sublabel: t(l.parentLabelId), icon: 'arrowRight' as const, onSelect: () => { closePalette(); navigate(l.route); } })) },
    {
      id: 'actions',
      title: t('palette.actions'),
      items: [
        { id: 'act-sidebar', label: t(sidebarCollapsed ? 'palette.expandMenu' : 'palette.collapseMenu'), icon: 'panelLeft' as const, onSelect: () => { closePalette(); toggleSidebar(); } },
        { id: 'act-signout', label: t('header.signOut'), icon: 'logOut' as const, onSelect: () => { closePalette(); void logout(); } },
      ].filter((a) => q === '' || textMatches(a.label, q)),
    },
  ];
  const paletteCount = sections.reduce((n, s) => n + s.items.length, 0);

  const crumbs: BreadcrumbItem[] = route === 'my_profile'
    ? [{ label: t('nav.profile') }]
    : unknownCrumb
      ? [{ label: t('nav.home') }]
      : isRecord
        ? [{ label: t(crumb.labelId), onClick: () => navigate(navRoute) }, { label: recordTitle ?? t('crumb.record') }]
        : [{ label: t(crumb.parentLabelId) }, { label: t(crumb.labelId) }];

  return (
    <>
      <ConsoleShell
        sidebarLabel={t('nav.aria')}
        menuLabel={t('header.openMenu')}
        closeMenuLabel={t('header.closeMenu')}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={toggleSidebar}
        collapseLabel={t('palette.collapseMenu')}
        expandLabel={t('palette.expandMenu')}
        sidebar={(close, { collapsed }) => (
          <SidebarNav
            label={t('nav.aria')}
            brand={{ title: t('chrome.brand') }}
            items={navItems}
            activeId={idByRoute.get(navRoute)}
            collapsed={collapsed}
            onSelect={(id) => {
              const target = routeById.get(id);
              if (target !== undefined) navigate(target);
              close();
            }}
            operator={{ name: operatorName, detail: operatorMail, initials, photoUrl: me?.photoUrl, profile: { label: t('nav.profile'), onOpen: () => { navigate('my_profile'); close(); } }, signOutLabel: t('header.signOut'), theme: { label: t('header.theme'), options: { system: t('header.theme.system'), light: t('header.theme.light'), dark: t('header.theme.dark') } }, onSignOut: () => void logout() }}
          />
        )}
        breadcrumb={<Breadcrumb ariaLabel={t('header.breadcrumb')} items={crumbs} />}
        search={
          <button type="button" className="vello-palette-trigger" onClick={openPalette}>
            <UiIcon name="search" size={15} />
            <span className="vello-palette-trigger__text">{t('palette.trigger')}</span>
            <Kbd>⌘K</Kbd>
          </button>
        }
        scope={<AdminScopeSwitcher />}
        actions={
          <PopoverAnchor>
            {/* D387: a 36px bell drawn in a 48px square hit area (was a 36px box). */}
            <button type="button" className="vello-header-btn vello-header-btn--icon" aria-expanded={bellOpen} aria-label={t('header.notifications')} title={t('header.notifications')} onClick={() => setBellOpen((o) => !o)}>
              <UiIcon name="bell" size={17} />
              {/* D615 (owner, 2026-10-17): the bell's number counts the operational queues only; setup has its own row and menu badge. */}
              <OperatorAlertsBadge total={alerts.total} atLeast={alerts.atLeast} />
            </button>
            <Popover open={bellOpen} onClose={() => setBellOpen(false)} width={360} top={50} role="dialog" ariaLabel={t('header.notifications')}>
              <div className="vello-menu__heading">{t('alerts.title')}</div>
              {/* D401: real counts from the six queues this operator can read — never the permanent "empty" note. */}
              {setupPending > 0 && <SetupAlertRow pending={setupPending} onOpen={() => { setBellOpen(false); navigate('platform_setup'); }} />}
              {setupPending === 0 && alerts.total === 0 && !alerts.degraded && alerts.alerts.every((a) => a.state === 'ready') && alerts.alerts.length > 0 ? (
                <div className="vello-menu__note vello-admin-a-plain-note">{t('alerts.empty')}</div>
              ) : (
                <OperatorAlertsList alerts={alerts.alerts} degraded={alerts.degraded} onOpen={(target) => { setBellOpen(false); navigate(target); }} />
              )}
            </Popover>
          </PopoverAnchor>
        }
      >
        {children}
      </ConsoleShell>

      <CommandPalette
        open={paletteOpen}
        onClose={closePalette}
        query={paletteQuery}
        onQuery={setPaletteQuery}
        sections={sections}
        placeholder={t('palette.placeholder')}
        emptyText={t('palette.empty', { q })}
        navigateHint={t('palette.navigate')}
        openHint={t('palette.open')}
        countText={t('palette.count', { count: String(paletteCount) })}
      />
    </>
  );
}
